import { Container, Box, Text, Image, Flex } from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import { LOMA_LANDING_DARK_TEAL } from "../constants/colors";
import { motion } from "framer-motion";
import {
    FaDiscord,
    FaInstagram,
    FaTiktok,
    FaYoutube,
    FaWhatsapp,
    FaRegEnvelope,
    FaFacebook,
    FaLinkedin,
} from "react-icons/fa";

interface ExampleCard {
    image: string;
    title: string;
    label: string;
    socialNetwork: string;
    avatar?: string;
    handle?: string;
}

const scrollingCards: ExampleCard[] = [
    {
        image: "/bicycles.png",
        title: "Peer-to-Peer Bike Exchange for a Beach Town Facebook Group",
        label: "Classifieds",
        socialNetwork: "Facebook",
    },
    {
        image: "/gaming.png",
        title: "Marketplace for Specialized Gaming Rigs Reviewed by a YouTuber",
        label: "Marketplace",
        socialNetwork: "YouTube",
    },
    {
        image: "/cpas.png",
        title: "LinkedIn Influencer Launches Directory of CPAs Specializing in their Niche",
        label: "Directory",
        socialNetwork: "LinkedIn",
    },
    {
        image: "/cars.png",
        title: "Auctions for Rare Imported Overland Brands for a Discord Server",
        label: "Auction Platform",
        socialNetwork: "Discord",
    },
    {
        image: "/reviews.png",
        title: "Foodie Instagrammer Launches Directory for Top Burger Stands in Oregon",
        label: "Directory",
        socialNetwork: "Instagram",
    },
    {
        image: "/boats.jpg",
        title: "Peer-to-Peer Boat Rentals for Local Marina Communities",
        label: "Marketplace",
        socialNetwork: "WhatsApp",
    },
    {
        image: "/gyms.png",
        title: "TikTok for Gym Managers Launches Auction Platform to Liquidate Used Inventory",
        label: "Auction Platform",
        socialNetwork: "TikTok",
    },
    {
        image: "/horses.jpg",
        title: "Horse Boarding Services for a Private WhatsApp Group",
        label: "Directory",
        socialNetwork: "WhatsApp",
    },
];

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const getSocialIcon = (platform: string) => {
    switch (platform) {
        case "Facebook": return <FaFacebook />;
        case "Instagram": return <FaInstagram />;
        case "LinkedIn": return <FaLinkedin />;
        case "TikTok": return <FaTiktok />;
        case "Discord": return <FaDiscord />;
        case "WhatsApp": return <FaWhatsapp />;
        case "YouTube": return <FaYoutube />;
        default: return <FaRegEnvelope />;
    }
};

const getSocialBadgeColor = (platform: string) => {
    switch (platform) {
        case "Facebook": return "#1877F2";
        case "Instagram": return "#E4405F";
        case "LinkedIn": return "#0A66C2";
        case "TikTok": return "#000000";
        case "Discord": return "#5865F2";
        case "WhatsApp": return "#25D366";
        case "YouTube": return "#FF0000";
        default: return "gray.500";
    }
};
interface CarouselCardProps {
    card: ExampleCard;
}

const CarouselCard: React.FC<CarouselCardProps> = ({ card }) => {
    return (
        <Box
            flex="0 0 auto"
            width="275px"
            padding={4}
            textAlign="left"
            display="flex"
            justifyContent="center"
            borderRadius="16px"
        >
            <Box width="185px" mx="auto">
                <MotionBox
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                >
                    <Image
                        src={card.image}
                        alt={card.title}
                        width="185px"
                        height="auto"
                        borderRadius="16px"
                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                        mb={4}
                        mx="auto"
                    />
                </MotionBox>
                <Box height="auto" display="flex" flexDirection="column" justifyContent="space-between">
                    {/* Row 1: Left-aligned Label and Social Icon */}
                    <Flex justify="flex-start" align="center" gap={2} mb={2}>
                        {card.socialNetwork && (
                            <Box
                                bg={getSocialBadgeColor(card.socialNetwork)}
                                color="white"
                                width="24px"
                                height="24px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="xs"
                            >
                                {getSocialIcon(card.socialNetwork)}
                            </Box>
                        )}
                        <Box
                            bg={LOMA_LANDING_DARK_TEAL}
                            color="white"
                            px={2}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="500"
                        >
                            {card.label}
                        </Box>
                    </Flex>

                    {/* Row 2: Avatar and Handle */}
                    {(card.avatar || card.handle) && (
                        <Flex justify="flex-start" align="center" gap={2} pt={2}>
                            {card.avatar && (
                                <Box
                                    width="24px"
                                    height="24px"
                                    borderRadius="full"
                                    bg="gray.100"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontSize="xs"
                                >
                                    {card.avatar}
                                </Box>
                            )}
                            {card.handle && (
                                <Text
                                    fontSize="xs"
                                    fontWeight="600"
                                    color="white"
                                >
                                    {card.handle}
                                </Text>
                            )}
                        </Flex>
                    )}

                    {/* Row 3: Title */}
                    <Text
                        fontSize="sm"
                        fontWeight="500"
                        color="white"
                        lineHeight="1.3"
                        textAlign="left"
                        pt={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                    >
                        {card.title}
                    </Text>
                </Box>
            </Box>


        </Box>
    );
};

const ExampleCarousels = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartTime, setDragStartTime] = useState(0);

    const timeoutRef = useRef<{
        timeoutId1?: NodeJS.Timeout;
        timeoutId2?: NodeJS.Timeout;
    }>({});

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => prev + 1);
        }, 4000); // Change slide every 4 seconds

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (currentSlide >= scrollingCards.length) {
            // Clear any existing timeouts first
            if (timeoutRef.current.timeoutId1) clearTimeout(timeoutRef.current.timeoutId1);
            if (timeoutRef.current.timeoutId2) clearTimeout(timeoutRef.current.timeoutId2);

            // Wait for animation to complete, then reset position instantly
            timeoutRef.current.timeoutId1 = setTimeout(() => {
                setIsTransitioning(false);
                setCurrentSlide(0);
                // Re-enable transition after the reset
                timeoutRef.current.timeoutId2 = setTimeout(() => {
                    setIsTransitioning(true);
                }, 50);
            }, 800);
        }
    }, [currentSlide, scrollingCards.length]);

    // Cleanup only on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current.timeoutId1) clearTimeout(timeoutRef.current.timeoutId1);
            if (timeoutRef.current.timeoutId2) clearTimeout(timeoutRef.current.timeoutId2);
        };
    }, []);


    const extendedCards = [...scrollingCards, ...scrollingCards.slice(0, 3)];

    // Manual navigation functions
    const goToNextSlide = () => {
        setIsTransitioning(true);
        setCurrentSlide((prev) => prev + 1);
    };

    const goToPrevSlide = () => {
        setIsTransitioning(true);
        setCurrentSlide((prev) => {
            if (prev === 0) {
                // Jump to the end of the real cards (not extended)
                return scrollingCards.length - 1;
            }
            return prev - 1;
        });
    };

    const goToSlide = (slideIndex: number) => {
        setIsTransitioning(true);
        setCurrentSlide(slideIndex);
    };

    // Touch/Mouse event handlers
    const handleStart = (clientX: number) => {
        setIsDragging(true);
        setDragStartX(clientX);
        setDragStartTime(Date.now());
    };

    const handleEnd = (clientX: number) => {
        if (!isDragging) return;

        const dragDistance = clientX - dragStartX;
        const dragTime = Date.now() - dragStartTime;
        const threshold = 50; // Minimum distance for swipe
        const maxTime = 300; // Maximum time for swipe (ms)

        if (Math.abs(dragDistance) > threshold && dragTime < maxTime) {
            if (dragDistance > 0) {
                goToPrevSlide();
            } else {
                goToNextSlide();
            }
        }

        setIsDragging(false);
        setDragStartX(0);
        setDragStartTime(0);
    };

    // Touch events
    const handleTouchStart = (e: React.TouchEvent) => {
        handleStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        handleEnd(e.changedTouches[0].clientX);
    };

    // Mouse events
    const handleMouseDown = (e: React.MouseEvent) => {
        handleStart(e.clientX);
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        handleEnd(e.clientX);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                goToPrevSlide();
            } else if (e.key === "ArrowRight") {
                goToNextSlide();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <MotionBox
            mt={16}
            mb={8}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
        >
            <MotionBox
                as={Container}
                maxW="1200px"
                textAlign="center"
                mb={8}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
            >
                <Text fontSize="lg" fontWeight="700" color="white" mb={4}>
                    We stick to what <Text as="u">we</Text> do best so you can stick to what{" "}
                    <Text as="u">you</Text> do best.
                </Text>
                <Text fontSize="lg" color="white">
                    Check out a few example use cases below:
                </Text>
            </MotionBox>

            <MotionBox
                position="relative"
                py={8}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
            >
                {/* Desktop: Sliding 3 cards */}
                <Box
                    display={{ base: "none", md: "block" }}
                    overflow="hidden"
                    width="889px" // 3 cards (275px each) + 2 gaps (32px each) = 814px
                    minH="300px"
                    mx="auto" // Center the viewport
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    cursor={isDragging ? "grabbing" : "grab"}
                    userSelect="none"
                >
                    <MotionBox
                        display="flex"
                        gap={8}
                        alignItems="center"
                        animate={{
                            x: `${-currentSlide * (275 + 32)}px` // 275px card width + 32px gap
                        }}
                        transition={isTransitioning ? {
                            duration: 0.8,
                            ease: "easeInOut",
                            type: "tween"
                        } : { duration: 0 }}
                        width="max-content"
                        py={4}
                    >
                        {extendedCards.map((card, index) => (
                            <CarouselCard key={index} card={card} />
                        ))}
                    </MotionBox>
                </Box>

                {/* Mobile: Single card with slide */}
                <Box
                    display={{ base: "block", md: "none" }}
                    overflow="hidden"
                    width="275px"
                    minH="300px"
                    mx="auto"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    cursor={isDragging ? "grabbing" : "grab"}
                    userSelect="none"
                >
                    <MotionBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        animate={{
                            x: `${-currentSlide * 275}px` // Updated to match new card width
                        }}
                        transition={isTransitioning ? {
                            duration: 0.8,
                            ease: "easeInOut",
                            type: "tween"
                        } : { duration: 0 }}
                        width="max-content"
                        py={4}
                    >
                        {extendedCards.map((card, index) => (
                            <CarouselCard key={index} card={card} />
                        ))}
                    </MotionBox>
                </Box>

                {/* Dot Navigation */}
                <MotionFlex
                    justify="center"
                    mt={8}
                    gap={2}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    viewport={{ once: true }}
                >
                    {scrollingCards.map((_, index) => (
                        <Box
                            key={index}
                            w={3}
                            h={3}
                            borderRadius="full"
                            bg={index === (currentSlide % scrollingCards.length) ? LOMA_LANDING_DARK_TEAL : "gray.300"}
                            cursor="pointer"
                            onClick={() => goToSlide(index)}
                            transition="all 0.3s ease"
                            _hover={{
                                bg: index === (currentSlide % scrollingCards.length) ? LOMA_LANDING_DARK_TEAL : "gray.400"
                            }}
                        />
                    ))}
                </MotionFlex>
            </MotionBox>
        </MotionBox>
    );
}

export default ExampleCarousels;